import { z } from 'zod';
import { NEED_KEYS, type NeedKey, type Needs } from './play';

// Health, as Lagos Life's 💊 Health (worked out from its public client code), in London: leave your needs in the
// red and you can fall ill. Mild sickness clears by itself after a good stretch, or with medicine from the
// pharmacy (delivered); left alone for a day it turns serious, and you can't work until a doctor treats you. Left
// serious for two days, you collapse and an ambulance takes you to hospital. The NHS is free at the point of use,
// so the GP costs nothing, but a prescription has England's prescription charge; all of it is content.
//
// The server checks each life once an hour: two or more needs in the red adds to "neglect" (one point per need in
// the red), a better hour takes one point off. From `sickFrom` neglect on, each hour has a growing chance of
// falling ill, and what you catch depends on which needs were let go.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
const pence = z.number().int().min(0).max(100_000_000);

export const Sickness = z.object({
  id,
  name: z.string().trim().min(2).max(40),
  emoji: z.string().trim().min(1).max(16),
  // what it is like, mild and serious
  mild: z.string().trim().min(5).max(160),
  serious: z.string().trim().min(5).max(160),
  // what brings it on: a need left in the red, or anything (when none of the others fit)
  cause: z.enum(['any', ...NEED_KEYS] as [NeedKey | 'any', ...(NeedKey | 'any')[]]),
});
export type Sickness = z.infer<typeof Sickness>;

export const HealthContent = z.object({
  visible: z.boolean(),
  sicknesses: z.array(Sickness).min(1).max(30),
  // a need below this is "in the red"; this many in the red makes an hour count against you
  redBelow: z.number().int().min(1).max(60),
  redNeeds: z.number().int().min(1).max(6),
  // falling ill: from this much neglect, a chance an hour that grows by `chanceStep` a point, up to `chanceCap`
  sickFrom: z.number().int().min(1).max(100),
  chanceStep: z.number().min(0).max(1),
  chanceCap: z.number().min(0).max(1),
  // getting better alone: this many hours in a row with every need at or above `wellAbove` clears mild sickness
  recoverHours: z.number().int().min(1).max(168),
  wellAbove: z.number().int().min(1).max(100),
  // untreated: mild turns serious, serious puts you in hospital (hours)
  seriousAfterHours: z.number().int().min(1).max(336),
  hospitalAfterHours: z.number().int().min(1).max(336),
  // treatment: the pharmacy's medicine, delivered (mild only); the GP's prescription (cures anything) at the
  // clinic, done by doing its activity there; and the hospital (its bill, and how long it keeps you, in seconds)
  pharmacyPence: pence,
  prescriptionPence: pence,
  clinicPlaceId: id,
  doctorActivityId: id,
  hospitalPence: pence,
  hospitalSeconds: z.number().int().min(10).max(3600),
});
export type HealthContent = z.infer<typeof HealthContent>;

const sickness = (s: Sickness) => s;
export const DEFAULT_HEALTH: HealthContent = {
  visible: true,
  sicknesses: [
    sickness({
      id: 'flu',
      name: 'Flu',
      emoji: '🤒',
      mild: 'Aching all over, hot and cold. Proper flu, not a cold.',
      serious: 'Flu has knocked you flat. You can barely get out of bed.',
      cause: 'any',
    }),
    sickness({
      id: 'cold',
      name: 'Heavy cold',
      emoji: '🤧',
      mild: 'Sneezing, blocked nose, run down from too little sleep.',
      serious: "It's gone to your chest and turned into a fever.",
      cause: 'energy',
    }),
    sickness({
      id: 'stomach_bug',
      name: 'Stomach bug',
      emoji: '🤢',
      mild: "Your stomach won't settle and you feel weak.",
      serious: 'The bug has taken over. You can’t keep anything down.',
      cause: 'hygiene',
    }),
    sickness({
      id: 'food_poisoning',
      name: 'Food poisoning',
      emoji: '🤮',
      mild: 'Something you ate is fighting back.',
      serious: "You've been running to the loo all day.",
      cause: 'hunger',
    }),
  ],
  redBelow: 20,
  redNeeds: 2,
  sickFrom: 8,
  chanceStep: 0.04,
  chanceCap: 0.3,
  recoverHours: 12,
  wellAbove: 50,
  seriousAfterHours: 24,
  hospitalAfterHours: 48,
  pharmacyPence: 1_250,
  prescriptionPence: 990,
  clinicPlaceId: 'health',
  doctorActivityId: 'health_gp',
  hospitalPence: 0,
  hospitalSeconds: 300,
};
export const healthOf = (content: { health?: HealthContent }) => content.health ?? DEFAULT_HEALTH;
export function healthProblems(health: HealthContent): string[] {
  const problems: string[] = [];
  const ids = new Set(health.sicknesses.map((item) => item.id));
  if (ids.size !== health.sicknesses.length) problems.push('Two sicknesses have the same id.');
  if (!health.sicknesses.some((item) => item.cause === 'any'))
    problems.push('One sickness must be for "anything" (when no single need was let go).');
  if (health.chanceCap < health.chanceStep) problems.push('The most likely chance is below one step.');
  return problems;
}

// ---------- the hourly check (the server runs it; tests and the screens use the same numbers) ----------

export const redNeedsOf = (needs: Needs, health: Pick<HealthContent, 'redBelow'>) =>
  NEED_KEYS.filter((key) => needs[key] < health.redBelow);
// neglect after one hour: a bad hour adds a point for each need in the red, a better one takes a point off
export function neglectAfter(neglect: number, needs: Needs, health: HealthContent) {
  const red = redNeedsOf(needs, health).length;
  return Math.max(0, neglect + (red >= health.redNeeds ? red : -1));
}
// the chance this hour of falling ill
export const sickChance = (neglect: number, health: HealthContent) =>
  neglect < health.sickFrom ? 0 : Math.min(health.chanceCap, (neglect - health.sickFrom + 2) * health.chanceStep);
// what is caught: from the needs let go (the lowest first), or the "anything" one; `roll` (0 to 1) picks
// between sicknesses with the same cause
export function sicknessFor(needs: Needs, health: HealthContent, roll: number): Sickness {
  const red = redNeedsOf(needs, health).sort((a, b) => needs[a] - needs[b]);
  for (const need of red) {
    const fits = health.sicknesses.filter((item) => item.cause === need);
    if (fits.length) return fits[Math.min(fits.length - 1, Math.floor(roll * fits.length))]!;
  }
  const any = health.sicknesses.filter((item) => item.cause === 'any');
  const pool = any.length ? any : health.sicknesses;
  return pool[Math.min(pool.length - 1, Math.floor(roll * pool.length))]!;
}
// every need high enough to count as a good hour for getting better
export const wellHour = (needs: Needs, health: HealthContent) =>
  NEED_KEYS.every((key) => needs[key] >= health.wellAbove);

// ---------- over the wire ----------

export const SickLevel = z.enum(['mild', 'serious']);
export type SickLevel = z.infer<typeof SickLevel>;
export const SickView = z.object({
  id: z.string(),
  name: z.string(),
  emoji: z.string(),
  level: SickLevel,
  // what it is like now
  text: z.string(),
  since: z.string(),
  // when it gets worse if left alone: serious (from mild), or hospital (from serious)
  worseAt: z.string(),
});
export type SickView = z.infer<typeof SickView>;
export const HealthView = z.object({
  sick: SickView.nullable(),
  // needs in the red now, and whether they are enough to count against you
  redNeeds: z.array(z.enum(NEED_KEYS as [NeedKey, ...NeedKey[]])),
  atRisk: z.boolean(),
  pharmacyPence: z.number().int(),
  prescriptionPence: z.number().int(),
  clinic: z.object({ placeId: z.string(), name: z.string(), here: z.boolean() }),
});
export type HealthView = z.infer<typeof HealthView>;
export const HealthReply = z.object({
  health: HealthView,
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type HealthReply = z.infer<typeof HealthReply>;
